import { SetStateAction, Dispatch, useEffect, useState } from 'react';
import { DevHubGrid } from '@devxdiscover/devhub-ui';
import { PartySearch } from '../common/PartySearch';
import { IOPSearch } from '../common/IOPSearch';
import { ResponsibleParty } from '../insights/types/RoleTypes';
import { AdditionalDetails, CorporateEntity } from './types/onboardingTypes';
import ApplicationGroupSearch from '../common/ApplicationGroupSearch';
import { ApplicationGroup } from '../insights/types/InsightsTypes';
import { ClassificationSelect } from '../common/ClassificationSelect';
import { InformationClassificationPolicy } from '../insights/types/PolicyTypes';
import {
  BusinessCapabilitiesSelect,
  BusinessCapability,
} from '../common/BusinessCapabilitiesSelect';
import {
  ServiceOfferingSelect,
  ServiceOffering,
} from '../common/ServiceOfferingSelect';
import {
  ServiceBusinessSelect,
  ServiceBusiness,
} from '../common/ServiceBusinessSelect';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

interface Props {
  entity: string;
  formData: AdditionalDetails;
  setFormData: Dispatch<SetStateAction<AdditionalDetails>>;
  setValidationHasError: Dispatch<SetStateAction<boolean>>;
}

/**
 * Component to select additional details for new app onboarding
 * @param props The properties passed to the component.
 * @param props.setFormData SetStateAction to pass form data back to stepper component
 * @param props.formData the data of the current form
 */
export const AppCreationAdditionalDetails = (props: Props) => {
  const { formData, setFormData, setValidationHasError } = props;
  const [selectedSPOC, setSelectedSPOC] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [iopOwner, setIopOwner] = useState<string>('');
  const [iopGroup, setIopGroup] = useState<string>('');
  const [infoClass, setInfoClass] = useState<InformationClassificationPolicy>(
    {} as InformationClassificationPolicy,
  );
  const [appGroup, setAppGroup] = useState<ApplicationGroup>(
    {} as ApplicationGroup,
  );
  const [businessCapabilities, setBusinessCapabilities] = useState<
    BusinessCapability[]
  >(formData.businessCapabilities ?? []);
  const [serviceOffering, setServiceOffering] =
    useState<ServiceOffering | null>(formData.serviceOffering ?? null);
  const [serviceBusiness, setServiceBusiness] =
    useState<ServiceBusiness | null>(formData.serviceBusiness ?? null);

  useEffect(() => {
    setBusinessCapabilities(formData.businessCapabilities ?? []);
  }, [formData.businessCapabilities]);

  useEffect(() => {
    setServiceOffering(formData.serviceOffering ?? null);
  }, [formData.serviceOffering]);

  useEffect(() => {
    setServiceBusiness(formData.serviceBusiness ?? null);
  }, [formData.serviceBusiness]);

  useEffect(() => {
    const prevDetails: AdditionalDetails = formData;

    if (iopOwner === 'clear' || iopOwner.length === 0) {
      prevDetails.iopOwner = '';
    } else {
      prevDetails.iopOwner = iopOwner;
    }

    if (iopGroup === 'clear' || iopGroup.length === 0) {
      prevDetails.iopGroup = '';
    } else {
      prevDetails.iopGroup = iopGroup;
    }

    if (selectedSPOC?.login) {
      if (selectedSPOC?.login === 'clear') {
        prevDetails.securityPoc = {} as ResponsibleParty;
      } else {
        prevDetails.securityPoc = selectedSPOC;
      }
    }

    if (appGroup?.name) {
      if (appGroup?.name === 'clear') {
        prevDetails.appGroup = {} as ApplicationGroup;
      } else {
        prevDetails.appGroup = appGroup;
      }
    }

    if (infoClass?.name) {
      if (infoClass?.name === 'clear') {
        prevDetails.infoClass = {} as InformationClassificationPolicy;
      } else {
        prevDetails.infoClass = infoClass;
      }
    }

    prevDetails.businessCapabilities = businessCapabilities;
    prevDetails.serviceOffering = serviceOffering;
    prevDetails.serviceBusiness = serviceBusiness;

    setFormData(prevDetails);

    const hasError =
      !iopOwner ||
      iopOwner === 'clear' ||
      !iopGroup ||
      iopGroup === 'clear' ||
      !infoClass?.name ||
      infoClass?.name === 'clear';
    setValidationHasError(hasError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    iopGroup,
    iopOwner,
    selectedSPOC,
    appGroup,
    infoClass,
    businessCapabilities,
    serviceOffering,
    serviceBusiness,
  ]);

  return (
    <DevHubGrid container spacing={2}>
      <DevHubGrid item md={12} lg={6}>
        <IOPSearch
          name="iopSupportOwner"
          groupEnabled={false}
          selectedParty={formData.iopOwner}
          setSelectedParty={setIopOwner}
          helperText="IOP group responsible for the application	"
          required
        />
      </DevHubGrid>
      <DevHubGrid item md={12} lg={6}>
        <IOPSearch
          name="iopSupportGroup"
          groupEnabled
          selectedParty={formData.iopGroup}
          setSelectedParty={setIopGroup}
          helperText="IOP group providing support for the application	"
          required
        />
      </DevHubGrid>
      <DevHubGrid item md={12} lg={6}>
        <PartySearch
          entity={CorporateEntity.CABLE}
          name="spoc"
          helperText="Individual security point of contact"
          groupEnabled={false}
          label="Security POC"
          setSelectedParty={setSelectedSPOC}
          selectedParty={formData.securityPoc}
          required={false}
        />
      </DevHubGrid>
      <DevHubGrid item md={12} lg={6}>
        <ApplicationGroupSearch
          label="Application Group"
          selectedAppGroup={formData?.appGroup ?? undefined}
          setAppGroup={setAppGroup}
          helperText="A grouping of one or more applications that share the same release timelines, dependencies, and resources"
          required={false}
        />
      </DevHubGrid>
      <DevHubGrid item md={12} lg={6}>
        <ClassificationSelect
          name="icp"
          size="small"
          label="Information Classification policy"
          helperText="Information Classification policy"
          setSelectedParty={setInfoClass}
          selectedParty={formData.infoClass}
          required
        />
      </DevHubGrid>
      {ConfigHelper.instance.isFeatureEnabled(
        Feature.BUSINESS_CAPABILITIES,
      ) && (
        <DevHubGrid item md={12} lg={6}>
          <BusinessCapabilitiesSelect
            selectedCapabilities={businessCapabilities}
            setSelectedCapabilities={setBusinessCapabilities}
            helperText="Select one or more business capabilities"
            required={false}
          />
        </DevHubGrid>
      )}
      <DevHubGrid item md={12} lg={6}>
        <ServiceOfferingSelect
          selectedServiceOffering={serviceOffering}
          setSelectedServiceOffering={setServiceOffering}
          helperText="Service offering associated with this application"
          required={false}
        />
      </DevHubGrid>
      <DevHubGrid item md={12} lg={6}>
        <ServiceBusinessSelect
          selectedServiceBusiness={serviceBusiness}
          setSelectedServiceBusiness={setServiceBusiness}
          helperText="Service business (CMDB) associated with this application"
          required={false}
        />
      </DevHubGrid>
    </DevHubGrid>
  );
};
